import { ref, computed } from 'vue';

import { useRouter, useRoute } from 'vue-router';
import { useI18n } from '@/i18n-shim';
import LocaleFiFI from './localization/fi-FI';
import LocaleSvFI from './localization/sv-FI';
import LocaleEnUS from './localization/en-US';
import { createI18n as actualCreateI18n} from 'vue-i18n'

export const allLanguages = ['fi-FI', 'sv-FI', 'en-US'] as const;

export type eSignLanguage = typeof allLanguages[number];

function eSignLanguageToHtmlAttribute(lang: eSignLanguage ): string {
  switch (lang) {
    case 'fi-FI':
      return 'fi';
    case 'sv-FI':
      return 'sv';
    case 'en-US':
      return 'en';
  }
}

export function stringToLanguage(lang: string | undefined | null): eSignLanguage | null {
  if (!lang)
    return null;
  if (allLanguages.includes(lang as eSignLanguage)) {
    return lang as eSignLanguage;
  } else {
    return null;
  }
}

function setHtmlLang(lang: eSignLanguage) {
  const htmlLang = eSignLanguageToHtmlAttribute(lang);
  if (htmlLang) { document.documentElement.setAttribute('lang', htmlLang); }
}

export function shortDisplayName(lang: eSignLanguage): string {
  switch (lang) {
    case 'fi-FI':
      return 'FI';
    case 'sv-FI':
      return 'SV';
    case 'en-US':
      return 'EN';
    default:
      return '';
  }
}

export function i18nWithLink(s: string, link: string): string {
  return s.replace(/\$(.*)\$/, `<a href="${link}" rel="nofollow">$1</a>`);
}


export function createI18n() {
  
  const urlParams = new URLSearchParams(window.location.search);
  const queryLanguage = urlParams.get('lang');
  const lang = stringToLanguage(queryLanguage) ?? 'fi-FI';
  setHtmlLang(lang);

  const vueI18n = actualCreateI18n({
    legacy: false,
    locale: lang,
    messages: {
      'fi-FI': LocaleFiFI,
      'sv-FI': LocaleSvFI,
      'en-US': LocaleEnUS,
    },
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    missing: (locale, key, vm, values) => '-------------------- [' + key + '] MISSING CONTENT --------------------',
    
  });

  //debug mode can be used to see that all strings in ui are translations
  // const debug = urlParams.get('debug');
  // if(debug) { vueI18n.formatter = new DebugI18nFormatter(vueI18n.formatter); }
  return vueI18n;
}
// class DebugI18nFormatter {
//   private formatter: any;

//   constructor(formatter: any) {
//     this.formatter = formatter;
//   }

//   public interpolate(message: any, values: any) {
//     return this.formatter.interpolate(message, values)
//       .map((s: string) => '[i18n string: ' + s + ']');
//   }
// }

//this is outside the useEsignCommon function because there should only be one instance of it, so that changes to it will effect all places where it is used
//" as unknown" and "as eSignLanguage[]" are used to make the type to be an array of eSignLanguage instead the more restrictivie type of allLanguages
const availableLanguages = ref<eSignLanguage[]>((allLanguages as unknown) as eSignLanguage[]);

export default function useEsignCommon() {
  const currentRoute = useRoute();
  const router = useRouter();

  const queryLanguage = computed(() => {
    return getFirstParam(currentRoute.query.lang);
  });

  function getFirstParam(param: (string | Array<string | null> | null)): string | null {
    if (!param) { return null; }
    const paramString = param as string;
    if (paramString) { return paramString; }
    const paramArray = param as Array<string | null>;
    return paramArray[0];
  }
  const i18n = useI18n();
  function setLanguage(lang: eSignLanguage) {
    router.replace({ query: { lang } });
    i18n.locale.value = lang;
    setHtmlLang(lang);
  }

  return {
    availableLanguages,
    queryLanguage,
    setLanguage,
    router,
    currentRoute,
    i18n
  }
}
